import { Form, message, Skeleton } from "antd";
import { AgentForm } from "./components/AgentForm";
import {
  AgentFormValues,
  parseResponseFormatSchema,
} from "./components/agentFormValues";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchAgent,
  updateAgent,
  AgentInput,
} from "@/lib/api/resources/agents";
import { useEffect } from "react";

export default function AgentsEdit() {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm<AgentFormValues>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: agent,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["agent", id],
    queryFn: () => fetchAgent(id as string),
    enabled: !!id,
  });

  useEffect(() => {
    if (agent) {
      form.setFieldsValue({
        name: agent.name,
        aiProfile: agent.aiProfileId ?? undefined,
        instructions: agent.instructions,
        allowedTools: agent.allowedTools,
        maxTokensPerSession: agent.maxTokensPerSession,
        overrideTemperature:
          agent.overrideInferenceOptions?.temperature ?? undefined,
        overrideMaxTokens:
          agent.overrideInferenceOptions?.maxTokens ?? undefined,
        overrideTopP: agent.overrideInferenceOptions?.topP ?? undefined,
        responseFormatSchema: agent.responseFormatSchema
          ? JSON.stringify(agent.responseFormatSchema, null, 2)
          : "",
      });
    }
  }, [agent, form]);

  const mutation = useMutation({
    mutationFn: (values: Partial<AgentInput>) =>
      updateAgent(id as string, values),
    onSuccess: () => {
      message.success("Agent updated successfully");
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["agent", id] });
      navigate("/agents");
    },
    onError: () => {
      message.error("Failed to update agent");
    },
  });

  const onFinish = (values: AgentFormValues) => {
    const payload: Partial<AgentInput> = {
      name: values.name,
      aiProfile: values.aiProfile,
      instructions: values.instructions,
      allowedTools: values.allowedTools ?? [],
      maxTokensPerSession: values.maxTokensPerSession ?? null,
      overrideTemperature: values.overrideTemperature ?? null,
      overrideMaxTokens: values.overrideMaxTokens ?? null,
      overrideTopP: values.overrideTopP ?? null,
      responseFormatSchema: parseResponseFormatSchema(
        values.responseFormatSchema,
      ),
    };
    mutation.mutate(payload);
  };

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-100 rounded-xl p-6 text-red-600 font-medium shadow-sm max-w-4xl mx-auto mt-6">
        Failed to load agent data. Please ensure the agent exists or try
        refreshing.
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-2 pb-12">
      <div className="mb-8 pl-1">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
          {isLoading ? "Edit Agent" : `Edit: ${agent?.name}`}
        </h1>
        <p className="text-gray-500 mt-2 text-sm max-w-2xl">
          Refine the system instructions, parameters, and tools for this agent
          to improve its performance and capabilities.
        </p>
      </div>
      {isLoading ? (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
          <Skeleton active paragraph={{ rows: 12 }} />
        </div>
      ) : (
        <AgentForm
          form={form}
          onFinish={onFinish}
          isSubmitting={mutation.isPending}
        />
      )}
    </div>
  );
}
